/**
 * The funnel's front door (Monetization v2, sc-6940).
 *
 * Every INSERT COIN routes through resolvePlayAccess() BEFORE a game
 * mounts. It returns a verdict the caller acts on — nothing else in the
 * shell decides who may play. The five branches:
 *
 *   entitled (active pass)          → 'play'         (no token spent)
 *   signed-in + tokens remaining    → 'play'         (one token spent)
 *   signed-in + exhausted           → 'paywall'      (stub until sc-6944)
 *   anon + free taste unused        → 'play'         (device-metered)
 *   anon + free taste used          → 'login'        (the wall)
 *
 * Fail-CLOSED to the player's benefit is NOT the rule here — the server
 * spend is the authority (sc-6939, itself fail-closed). If the wallet or
 * spend call throws, we do NOT hand out a free run: signed-in players
 * see the paywall/retry, anon falls back to their device-taste check.
 */

import { consumeTaste, getWallet, spendToken } from '../api/client.js';
import { isSignedIn } from '../auth/session.js';

export type PlayVerdict =
  | { grant: 'play'; access: 'pass' | 'token' | 'free-taste' }
  | { grant: 'login' }
  | { grant: 'paywall'; resetsAtUtc?: string }
  | { grant: 'error' };

export async function resolvePlayAccess(): Promise<PlayVerdict> {
  if (!isSignedIn()) {
    // Anonymous: consume the daily free taste ATOMICALLY server-side
    // (sc-6940). Reading a client-cached meter raced — two rapid
    // launches both played free. The server is the single authority.
    // A throw (503 fail-closed / network) → login wall, never a free run.
    try {
      const taste = await consumeTaste();
      return taste.allowed
        ? { grant: 'play', access: 'free-taste' }
        : { grant: 'login' };
    } catch {
      return { grant: 'login' };
    }
  }

  // Signed in: wallet decides. A pass is unlimited; otherwise spend a
  // token. Server is authoritative and atomic (sc-6939).
  try {
    const wallet = await getWallet();
    if (wallet.entitlement) {
      return { grant: 'play', access: 'pass' };
    }
    const spend = await spendToken();
    if (spend.allowed) {
      return { grant: 'play', access: spend.unlimited ? 'pass' : 'token' };
    }
    // allowed:false ⇒ exhausted (a 200, not a throw).
    return { grant: 'paywall', resetsAtUtc: wallet.resetsAtUtc };
  } catch {
    // Wallet/spend threw (incl. the 503 fail-closed path). Never a free
    // run for a signed-in player — send them to the paywall/retry.
    return { grant: 'error' };
  }
}
